import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Save, Loader2, QrCode, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useAuth } from "@/context/AuthContext";
import { BongkarConfigForm } from "@/components/admin/TxExtras";
import { SeoForm, MarqueeForm } from "@/components/admin/SeoSettings";
import { NickCheckSettings } from "@/components/admin/NickCheckSettings";

const MAX_BYTES = 2 * 1024 * 1024;
const SLOT_LABEL = { all: "Semua Game", royal_dream: "Royal Dream", higgs_domino: "Higgs Domino" };

const QrisSlot = ({ slot, image, canManage, reload }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      await reload();
      toast.success(ok);
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan QRIS"));
    } finally {
      setBusy(false);
    }
  };
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Format harus PNG, JPG, atau WEBP");
    if (file.size > MAX_BYTES) return toast.error("Ukuran gambar maksimal 2MB");
    const reader = new FileReader();
    reader.onload = () => run(() => api.post("/admin/qris", { image: reader.result }, { params: { slot } }), `QRIS ${SLOT_LABEL[slot]} berhasil diupload`);
    reader.readAsDataURL(file);
  };
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-white/5 bg-[#0d0d12] p-4" data-testid={`qris-slot-${slot}`}>
      <div className="text-xs font-bold uppercase tracking-widest text-[#e5b80b]">QRIS {SLOT_LABEL[slot]}</div>
      <div className="grid h-40 w-40 place-items-center overflow-hidden rounded-xl bg-white">
        {image ? <img src={image} alt={`QRIS ${SLOT_LABEL[slot]}`} className="h-full w-full object-contain" data-testid={`qris-preview-${slot}`} />
          : <span className="px-3 text-center text-[11px] text-zinc-500" data-testid={`qris-empty-${slot}`}>Belum ada QRIS</span>}
      </div>
      {canManage ? (
        <div className="flex flex-wrap justify-center gap-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid={`qris-file-input-${slot}`} />
          <button onClick={() => inputRef.current?.click()} disabled={busy} className="rk-btn-primary inline-flex items-center gap-1.5 !px-3 !py-1.5 text-xs" data-testid={`qris-upload-btn-${slot}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {image ? "Ganti" : "Upload"}
          </button>
          {image ? (
            <button onClick={() => run(() => api.delete("/admin/qris", { params: { slot } }), `QRIS ${SLOT_LABEL[slot]} dihapus`)} disabled={busy} className="rk-btn-ghost inline-flex items-center gap-1 !px-3 !py-1.5 text-xs text-rose-400" data-testid={`qris-delete-btn-${slot}`}>
              <Trash2 size={12} /> Hapus
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const QrisSettings = ({ canManage }) => {
  const { qris_mode, qris_image, qris_images, reload } = useSiteSettings();
  const setMode = async (mode) => {
    try {
      await api.put("/admin/settings", { qris_mode: mode });
      await reload();
      toast.success(mode === "single" ? "Mode: 1 QRIS untuk semua game" : "Mode: QRIS per game");
    } catch (err) {
      toast.error(errMsg(err, "Gagal mengubah mode QRIS"));
    }
  };
  return (
    <div className="rk-glass rounded-2xl p-6 lg:col-span-2" data-testid="qris-uploader">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-bold"><QrCode size={18} className="text-[#e5b80b]" /> Gambar QRIS</h3>
          <p className="mt-1 text-xs text-zinc-500">Tampil di popup pembayaran top up. PNG/JPG/WEBP, maks 2MB. Pada mode per game, game yang belum punya QRIS memakai QRIS semua game.</p>
        </div>
        <div className="flex rounded-xl border border-white/10 p-1" data-testid="qris-mode">
          {[["single", "1 QRIS Semua Game"], ["per_game", "QRIS per Game"]].map(([k, l]) => (
            <button key={k} disabled={!canManage} onClick={() => setMode(k)} data-testid={`qris-mode-${k}`}
              className={`rounded-lg px-3 py-1.5 text-xs transition-colors ${qris_mode === k ? "bg-[#e5b80b] font-semibold text-black" : "text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className={`mt-5 grid gap-4 ${qris_mode === "per_game" ? "sm:grid-cols-3" : "sm:max-w-xs"}`}>
        <QrisSlot slot="all" image={qris_image} canManage={canManage} reload={reload} />
        {qris_mode === "per_game" ? ["royal_dream", "higgs_domino"].map((g) => (
          <QrisSlot key={g} slot={g} image={qris_images?.[g]} canManage={canManage} reload={reload} />
        )) : null}
      </div>
      {!canManage ? <p className="mt-3 text-xs text-zinc-500" data-testid="settings-readonly-note">Mode lihat saja.</p> : null}
    </div>
  );
};

const LINKS = [
  ["support_wa", "WhatsApp Admin Komplain", "628xxxxxxxxxx"],
  ["wa_channel", "Join Saluran", "https://whatsapp.com/channel/..."],
  ["bongkar_otomatis_url", "Bongkar Otomatis", "https://..."],
  ["live_chat_url", "Live Chat", "https://..."],
  ["telegram_url", "Telegram", "https://t.me/..."],
];

const linkError = (k, v) => {
  if (!v.trim()) return k === "support_wa" ? "Wajib diisi" : "";
  if (k === "support_wa") return /^(62|0)\d{8,14}$/.test(v.replace(/\D/g, "")) ? "" : "Nomor WA tidak valid";
  return /^https:\/\/\S{4,}$/.test(v.trim()) ? "" : "Harus diawali https://";
};

const ContactForm = ({ canManage }) => {
  const s = useSiteSettings();
  const pick = () => Object.fromEntries(LINKS.map(([k]) => [k, s[k] || ""]));
  const [f, setF] = useState(pick);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setF(pick());
  }, [s.support_wa, s.wa_channel, s.bongkar_otomatis_url, s.live_chat_url, s.telegram_url]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalid = LINKS.some(([k]) => linkError(k, f[k]));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", f);
      await s.reload();
      toast.success("Kontak & link berhasil disimpan");
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="rk-glass rounded-2xl p-6 lg:col-span-2" data-testid="contact-settings-form">
      <h3 className="flex items-center gap-2 text-lg font-bold"><Link2 size={18} className="text-[#e5b80b]" /> Link & Tombol Kontak</h3>
      <p className="mt-1 text-xs text-zinc-500">Tujuan tombol di halaman depan. Kosongkan link opsional untuk menyembunyikan tombolnya.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {LINKS.map(([k, label, ph]) => (
          <label key={k} className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500">{label}</span>
            <input value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} disabled={!canManage} placeholder={ph} className="rk-input mt-2" data-testid={`settings-${k}-input`} />
            {f[k] && linkError(k, f[k]) ? <span className="mt-1 block text-[11px] text-rose-400">{linkError(k, f[k])}</span> : null}
          </label>
        ))}
      </div>
      {canManage ? (
        <button type="submit" disabled={busy || invalid} className="rk-btn-primary mt-5 inline-flex items-center gap-2 text-sm disabled:opacity-50" data-testid="settings-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan
        </button>
      ) : null}
    </form>
  );
};

export const SettingsPanel = () => {
  const { can } = useAuth();
  const canManage = can("settings", "manage");
  return (
    <section className="grid gap-5 lg:grid-cols-2" data-testid="admin-settings-panel">
      <QrisSettings canManage={canManage} />
      <ContactForm canManage={canManage} />
      <BongkarConfigForm canManage={canManage} />
      <NickCheckSettings canManage={canManage} />
      <SeoForm canManage={canManage} />
      <MarqueeForm canManage={canManage} />
    </section>
  );
};
